"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import { resolveJsonToCsvDelimiter } from "@/lib/tool-kit/developer/csv-table";

type Direction = "csv-to-json" | "json-to-csv";
type DelimiterChoice = "auto" | "comma" | "semicolon" | "tab";
type IndentChoice = "2" | "4" | "tab";

const DELIMITER_CHARS: Record<Exclude<DelimiterChoice, "auto">, string> = {
  comma: ",",
  semicolon: ";",
  tab: "\t",
};

const CANDIDATE_DELIMITERS = [",", ";", "\t"];
const JSON_ERROR_POSITION_REGEX = /position (\d+)/;
const NEWLINE_REGEX = /\n/g;

function lineOfPosition(text: string, position: number): number {
  const before = text.slice(0, position);
  const matches = before.match(NEWLINE_REGEX);
  return (matches === null ? 0 : matches.length) + 1;
}

function detectDelimiter(text: string): string {
  const firstLine = text.split("\n", 1)[0];
  let best = ",";
  let bestCount = -1;
  for (const candidate of CANDIDATE_DELIMITERS) {
    const count = firstLine.split(candidate).length - 1;
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }
  return best;
}

function readQuotedField(
  text: string,
  start: number
): { value: string; end: number; closed: boolean } {
  let value = "";
  let i = start;
  while (i < text.length) {
    const char = text[i];
    if (char === '"') {
      if (text[i + 1] === '"') {
        value += '"';
        i += 2;
        continue;
      }
      return { value, end: i + 1, closed: true };
    }
    value += char;
    i += 1;
  }
  return { value, end: i, closed: false };
}

/** Small state-machine CSV parser — handles quoted fields containing
 *  delimiters, doubled quotes, and embedded newlines (RFC 4180 style). */
function parseCsv(
  text: string,
  delimiter: string,
  strings: typeof CSV_TO_JSON_STRINGS
): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let i = 0;

  function endField() {
    row.push(field);
    field = "";
  }

  function endRow() {
    endField();
    rows.push(row);
    row = [];
  }

  while (i < text.length) {
    const char = text[i];
    if (char === '"' && field.length === 0) {
      const quoted = readQuotedField(text, i + 1);
      if (!quoted.closed) {
        throw new Error(
          t(strings.unterminatedQuotedField, { line: lineOfPosition(text, i) })
        );
      }
      field = quoted.value;
      i = quoted.end;
      continue;
    }
    if (char === delimiter) {
      endField();
      i += 1;
      continue;
    }
    if (char === "\r") {
      endRow();
      i += text[i + 1] === "\n" ? 2 : 1;
      continue;
    }
    if (char === "\n") {
      endRow();
      i += 1;
      continue;
    }
    field += char;
    i += 1;
  }
  if (field.length > 0 || row.length > 0) {
    endRow();
  }
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

function csvToJson(
  text: string,
  delimiter: string,
  firstRowHeaders: boolean,
  indent: string | number,
  strings: typeof CSV_TO_JSON_STRINGS
): string {
  const rows = parseCsv(text, delimiter, strings);
  if (rows.length === 0) {
    throw new Error(strings.noDataRows);
  }
  if (!firstRowHeaders) {
    return JSON.stringify(rows, null, indent);
  }
  const [headers, ...dataRows] = rows;
  const objects = dataRows.map((row) => {
    const obj: Record<string, string> = {};
    headers.forEach((header, index) => {
      obj[header] = row[index] ?? "";
    });
    return obj;
  });
  return JSON.stringify(objects, null, indent);
}

function escapeCsvField(value: string, delimiter: string): string {
  const needsQuoting =
    value.includes(delimiter) ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r");
  if (!needsQuoting) {
    return value;
  }
  return `"${value.replaceAll('"', '""')}"`;
}

function cellToString(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function collectKeys(items: Record<string, unknown>[]): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    for (const key of Object.keys(item)) {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }
  return keys;
}

function assertObjectArray(
  parsed: unknown,
  strings: typeof CSV_TO_JSON_STRINGS
): Record<string, unknown>[] {
  if (!Array.isArray(parsed)) {
    throw new Error(strings.expectedJsonArray);
  }
  parsed.forEach((item, index) => {
    if (typeof item !== "object" || item === null || Array.isArray(item)) {
      throw new Error(t(strings.itemNotObject, { index }));
    }
  });
  return parsed as Record<string, unknown>[];
}

function jsonToCsv(
  text: string,
  delimiter: string,
  strings: typeof CSV_TO_JSON_STRINGS
): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    const positionMatch = message.match(JSON_ERROR_POSITION_REGEX);
    if (positionMatch !== null) {
      const line = lineOfPosition(text, Number(positionMatch[1]));
      throw new Error(t(strings.invalidJsonAroundLine, { line, message }));
    }
    throw new Error(t(strings.invalidJson, { message }));
  }
  const items = assertObjectArray(parsed, strings);
  if (items.length === 0) {
    throw new Error(strings.emptyJsonArray);
  }
  const keys = collectKeys(items);
  const headerLine = keys
    .map((key) => escapeCsvField(key, delimiter))
    .join(delimiter);
  const lines = items.map((item) =>
    keys
      .map((key) => escapeCsvField(cellToString(item[key]), delimiter))
      .join(delimiter)
  );
  return [headerLine, ...lines].join("\n");
}

function resolveDelimiter(choice: DelimiterChoice, input: string): string {
  if (choice === "auto") {
    return detectDelimiter(input);
  }
  return DELIMITER_CHARS[choice];
}

function resolveIndent(choice: IndentChoice): string | number {
  if (choice === "tab") {
    return "\t";
  }
  return Number(choice);
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** English strings. A translated content file overrides these through `ui`. */
export const CSV_TO_JSON_STRINGS = {
  direction: "Direction",
  csvToJsonLabel: "CSV → JSON",
  jsonToCsvLabel: "JSON → CSV",
  autoDetect: "Auto-detect",
  commaLabel: "Comma (,)",
  semicolonLabel: "Semicolon (;)",
  tabLabel: "Tab",
  spaces2: "2 spaces",
  spaces4: "4 spaces",
  delimiterLabel: "Delimiter:",
  jsonIndentLabel: "JSON indent:",
  firstRowHeaders: "First row is headers (produce objects)",
  csvInput: "CSV input",
  jsonInput: "JSON input",
  csvPlaceholder: "name,year\nAPA Manual,2020\nMLA Handbook,2021",
  jsonPlaceholder: '[{"name": "APA Manual", "year": 2020}]',
  convert: "Convert",
  copied: "Copied!",
  copyResult: "Copy Result",
  downloadFile: "Download {extension}",
  jsonExt: ".json",
  csvExt: ".csv",
  clear: "Clear",
  jsonOutput: "JSON output",
  csvOutput: "CSV output",
  unterminatedQuotedField: "Unterminated quoted field starting on line {line}.",
  noDataRows: "No data rows found in the CSV input.",
  expectedJsonArray:
    "Expected a JSON array of objects at the top level, e.g. [{...}, {...}].",
  itemNotObject:
    "Item at index {index} is not an object — JSON → CSV needs an array of flat objects.",
  invalidJsonAroundLine: "Invalid JSON (around line {line}): {message}",
  invalidJson: "Invalid JSON: {message}",
  emptyJsonArray: "The JSON array is empty — nothing to convert.",
} as const;

export default function CsvToJsonWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("csv-to-json");
  const [delimiterChoice, setDelimiterChoice] =
    useState<DelimiterChoice>("auto");
  const [indentChoice, setIndentChoice] = useState<IndentChoice>("2");
  const [firstRowHeaders, setFirstRowHeaders] = useState(true);
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(CSV_TO_JSON_STRINGS);

  const directions: { value: Direction; label: string }[] = [
    { value: "csv-to-json", label: strings.csvToJsonLabel },
    { value: "json-to-csv", label: strings.jsonToCsvLabel },
  ];
  const delimiterChoices: { value: DelimiterChoice; label: string }[] = [
    { value: "auto", label: strings.autoDetect },
    { value: "comma", label: strings.commaLabel },
    { value: "semicolon", label: strings.semicolonLabel },
    { value: "tab", label: strings.tabLabel },
  ];
  const indentChoices: { value: IndentChoice; label: string }[] = [
    { value: "2", label: strings.spaces2 },
    { value: "4", label: strings.spaces4 },
    { value: "tab", label: strings.tabLabel },
  ];

  function handleConvert() {
    if (input.trim().length === 0) {
      return;
    }
    try {
      const delimiter =
        direction === "csv-to-json"
          ? resolveDelimiter(delimiterChoice, input)
          : resolveJsonToCsvDelimiter(delimiterChoice);
      const result =
        direction === "csv-to-json"
          ? csvToJson(
              input,
              delimiter,
              firstRowHeaders,
              resolveIndent(indentChoice),
              strings
            )
          : jsonToCsv(input, delimiter, strings);
      setOutput(result);
      setError("");
      setCopied(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setOutput("");
    }
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleDownload() {
    if (output.length === 0) {
      return;
    }
    if (direction === "csv-to-json") {
      downloadFile(output, "data.json", "application/json");
    } else {
      downloadFile(output, "data.csv", "text/csv");
    }
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setError("");
    setCopied(false);
  }

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {directions.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {d.label}
            </label>
          ))}
        </div>
      </section>

      {/* Options */}
      <section className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <label className="flex items-center gap-2 text-foreground text-sm">
          {strings.delimiterLabel}
          <select
            className="rounded-md border border-border bg-card px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={(e) =>
              setDelimiterChoice(e.target.value as DelimiterChoice)
            }
            value={delimiterChoice}
          >
            {delimiterChoices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        {direction === "csv-to-json" && (
          <label className="flex items-center gap-2 text-foreground text-sm">
            {strings.jsonIndentLabel}
            <select
              className="rounded-md border border-border bg-card px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              onChange={(e) => setIndentChoice(e.target.value as IndentChoice)}
              value={indentChoice}
            >
              {indentChoices.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {direction === "csv-to-json" && (
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={firstRowHeaders}
              className="accent-primary"
              onChange={(e) => setFirstRowHeaders(e.target.checked)}
              type="checkbox"
            />
            {strings.firstRowHeaders}
          </label>
        )}
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="convert-input"
        >
          {direction === "csv-to-json" ? strings.csvInput : strings.jsonInput}
        </label>
        <textarea
          className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="convert-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={
            direction === "csv-to-json"
              ? strings.csvPlaceholder
              : strings.jsonPlaceholder
          }
          value={input}
        />
      </div>

      {/* Error */}
      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 font-mono text-destructive text-sm">
          {error}
        </p>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleConvert}
          type="button"
        >
          {strings.convert}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleDownload}
          type="button"
        >
          {t(strings.downloadFile, {
            extension:
              direction === "csv-to-json" ? strings.jsonExt : strings.csvExt,
          })}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="convert-output"
          >
            {direction === "csv-to-json"
              ? strings.jsonOutput
              : strings.csvOutput}
          </label>
          <textarea
            className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="convert-output"
            readOnly
            value={output}
          />
        </div>
      )}
    </>
  );
}
